import React, { useMemo, useRef, useState } from 'react';
import { View } from 'react-native';

import ColorWrapper from '@styles/colors';

import Image from '@v2/context/Image/context';
import JobCardInfoModal from './JobCardInfoModal';
import UnlinkedBatchModal from './UnlinkedBatchModal';
import TouchableElement from '@components/common/touchableElement';
import Svg from '@styles/icons/svg';
import JobCardMenu from '@v2/pages/planboard/components/Board/web/Card/Menu/JobCardMenu';
import SimpleButton, { SimpleButtonVariant } from '@components/operatorPanel/components/elements/buttons/SimpleButton';
import { JobCardImageProps } from '@v2/pages/planboard/types';
import { DOCUMENT_ENTITY_TYPE, IMAGE_HANDLING_CONTEXT } from '@components/imageHandling/common';
import { getBoxShadowStyle } from '@styles/shadow';
import { usePlanboardJobNavigation } from '@v2/pages/planboard/hooks/usePlanboardJobNavigation';

const JobCardImage: React.FC<JobCardImageProps> = ({ scale, C, font, item, onMenuOpen, onMoveToPosition, onLinkBatch, onMarkAsFinished, isCardHovered, onHoverChange }) => {
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [isLocalHovered, setIsLocalHovered] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isHovered = isLocalHovered || isCardHovered;
  const [isUnlinkedBatchModalOpen, setIsUnlinkedBatchModalOpen] = useState(false);
  const infoButtonRef = useRef<View>(null);
  const menuButtonRef = useRef<View>(null);
  const linkBatchButtonRef = useRef<View>(null);
  const { navigateToDocuments } = usePlanboardJobNavigation(item);

  // Check if batch is linked to a job (any NC file with productionBatchId)
  const isBatchNotLinked = item.linkedNcFiles?.every(ncFile => ncFile.productionBatchId == null) ?? true;

  const imageData = useMemo(() => {
    return {
      image: {
        documentId: item.imageDocumentId,
        imageHash: item.imageHash,
        imageData: {
          isCropped: item.imageData?.isCropped ?? false,
          extensionDerivative: item.imageData?.extensionDerivative,
          pageNumber: item.imageData?.pageNumber,
          rotationAngle: item.imageData?.rotationAngle || 0,
          rotationAngleCrop: item.imageData?.rotationAngle || 0,
        },
        width: scale(266),
        height: scale(176),
        contextId: item.productionOperationId,
        contextCode: IMAGE_HANDLING_CONTEXT[DOCUMENT_ENTITY_TYPE.ProductionOrder],
        isProcessing: false,
      },
      showImageControl: false,
      noOverlay: true,
      disableImageOverlay: true,
    };
  }, [item, scale]);

  return (
    <View
      style={{
        position: 'relative',
        width: '100%',
        height: scale(176),
        backgroundColor: C('Background/Light/System [mobile]', false),
        borderRadius: scale(4),
        overflow: 'hidden',
      }}
      onPointerEnter={() => {
        setIsLocalHovered(true);
        onHoverChange?.(true);
      }}
      onPointerLeave={() => {
        setIsLocalHovered(false);
        onHoverChange?.(false);
      }}
    >
      <View style={{ width: '100%' }}>
        <Image {...imageData} onPress={navigateToDocuments} />
      </View>

      {/* Link Batch Button - only for RUNNING jobs with batch not linked */}
      {item.status === 'Running' && isBatchNotLinked && !isInfoOpen && onLinkBatch && (
        <View
          ref={linkBatchButtonRef}
          style={{ position: 'absolute', top: scale(9), left: scale(9), zIndex: 4 }}
        >
          <SimpleButton
            C={C}
            scale={scale}
            font={font}
            variant={SimpleButtonVariant.Fill}
            text="Link Batch to Job"
            onPress={() => setIsUnlinkedBatchModalOpen(true)}
            style={{ height: scale(28) }}
          />
        </View>
      )}

      {onLinkBatch && (
         <UnlinkedBatchModal
            isOpen={isUnlinkedBatchModalOpen}
            onClose={() => setIsUnlinkedBatchModalOpen(false)}
            item={item}
            anchorRef={linkBatchButtonRef}
            onLinkBatch={onLinkBatch}
            scale={scale}
            font={font}
          />
      )}

      <View
        ref={infoButtonRef}
        style={{
          position: 'absolute',
          top: scale(9),
          left: scale(9),
          zIndex: 3,
        }}
      >
        <TouchableElement onPress={() => setIsInfoOpen((p) => !p)}>
          {({ pressHoverColor, boxStyle }) => {
            return (
              <View
                style={[
                  boxStyle,
                  {
                    backgroundColor: C(pressHoverColor(isInfoOpen ? 'Gray/Light/6' : 'Label/Light/AlwaysLight Primary')),
                    borderRadius: '50%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: scale(28),
                    height: scale(28),
                    // Show info button when: modal open, OR hovered (except Running jobs with batch not linked - Link Batch button takes that spot)
                    display: isInfoOpen || (isHovered && !(item.status === 'Running' && isBatchNotLinked)) ? 'flex' : 'none',
                  },
                  isInfoOpen ? {} : getBoxShadowStyle({ type: 'Symmetric' }),
                ]}
              >
                <Svg icon="planboardInfo" width={scale(8)} height={scale(15)} />
              </View>
            );
          }}
        </TouchableElement>
        <JobCardInfoModal
          isOpen={isInfoOpen}
          onClose={() => setIsInfoOpen(false)}
          item={item}
          anchorRef={infoButtonRef}
          scale={scale}
          font={font}
        />
      </View>

      {/* Not Linked Indicator - only for RUNNING jobs with batch not linked, hidden on hover */}
      {item.status === 'Running' && isBatchNotLinked && !isHovered && !isMenuOpen && onLinkBatch && (
        <View style={{ position: 'absolute', top: scale(9), right: scale(9), zIndex: 3 }}>
          <Svg icon="notLinked" width={scale(28)} height={scale(28)} />
        </View>
      )}

      <View
        ref={menuButtonRef}
        style={{
          position: 'absolute',
          right: scale(9),
          top: scale(9),
          zIndex: 3,
        }}
      >
        <TouchableElement
          onPress={(e) => {
            e?.stopPropagation?.();
            setIsMenuOpen((p) => !p);
          }}
        >
          {({ pressHoverColor, boxStyle }) => (
            <View
              style={[
                boxStyle,
                {
                  backgroundColor: C(pressHoverColor(isMenuOpen ? 'Gray/Light/6' : 'Label/Light/AlwaysLight Primary')),
                  borderRadius: '50%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: scale(28),
                  height: scale(28),
                  display: isMenuOpen || isHovered ? 'flex' : 'none',
                },
                isMenuOpen ? {} : getBoxShadowStyle({ type: 'Symmetric' }),
              ]}
            >
              <Svg icon="ellipsis" width={scale(14)} height={scale(4)} />
            </View>
          )}
        </TouchableElement>
        <JobCardMenu
          isOpen={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          onOpenJob={() => {
            onMenuOpen?.();
          }}
          onMoveToPosition={onMoveToPosition}
          onMarkAsFinished={onMarkAsFinished}
          anchorRef={menuButtonRef}
          scale={scale}
          font={font}
          C={C}
        />
      </View>
    </View>
  );
};

export default ColorWrapper(JobCardImage);
